import React, { useState, useEffect, useCallback } from 'react';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import { FiLink } from 'react-icons/fi';
import { getAuthContext } from '../../utils/jwt';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

// Type-cast FiLink icon to ensure compatibility with React's component props
const FiLinkComponent = FiLink as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Interface for trends data from API - matches exact response
interface TrendDataPoint {
  clicks: number;
  end: string;
  start: string;
  opens: number;
}

// Interface for top links from API - matches exact response
interface TopLinksResponse {
  items: TopLinkData[];
}

interface TopLinkData {
  label: string;
  campaignName: string;
  rightBadge: {
    value: number;
    label: string;
  };
  linkId: string;
  campaignId: string;
}

// Interface for top performing links display
interface TopLinkDisplay {
  title: string;
  rate: string;
  label: string;
  campaignName: string;
  campaignId: string;
  linkId: string;
}

// MiddleSection component displays top-performing links and a line chart for campaign trends
const MiddleSection: React.FC = () => {
  // Manages the selected time range for the campaign trends chart
  // Default is 'PT24H' (Last 24 Hours)
  const [selectedRange, setSelectedRange] = useState<'PT1H' | 'PT24H' | 'PT168H' | 'PT720H' | 'PT8760H'>('PT720H');

  // State for trends data
  const [trendsData, setTrendsData] = useState<TrendDataPoint[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for top links data
  const [topLinksData, setTopLinksData] = useState<TopLinkData[]>([]);
  const [topLinksLoading, setTopLinksLoading] = useState<boolean>(true);
  const [topLinksError, setTopLinksError] = useState<string | null>(null);

  // State for selected campaign ID (from top links)
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  // Function to get grain value based on selected range
  const getGrain = useCallback(() => {
    return selectedRange;
  }, [selectedRange]);

  // Function to get date range based on selected filter
  const getDateRange = useCallback(() => {
    const now = new Date();
    let from: Date;

    switch (selectedRange) {
      case 'PT1H':
        from = new Date(now);
        from.setHours(from.getHours() - 1);
        break;
      case 'PT24H':
        from = new Date(now);
        from.setDate(from.getDate() - 1);
        break;
      case 'PT168H':
        from = new Date(now);
        from.setDate(from.getDate() - 7);
        break;
      case 'PT720H':
        from = new Date(now);
        from.setDate(from.getDate() - 30);
        break;
      case 'PT8760H':
        from = new Date(now);
        from.setFullYear(from.getFullYear() - 1);
        break;
      default:
        from = new Date(now);
        from.setHours(from.getHours() - 1);
        break;
    }

    return {
      from: from.toISOString(),
      to: now.toISOString(),
    };
  }, [selectedRange]);

  // Function to fetch top links data
  const fetchTopLinks = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setTopLinksError('Authentication required');
      setTopLinksLoading(false);
      return;
    }

    setTopLinksLoading(true);
    setTopLinksError(null);

    try {
      const { from, to } = getDateRange();
      const grain = getGrain();

      // Construct the API URL with query parameters
      const url = `${BASE_URL}/v1/reporting/analytics/top-links?tenantId=${tenantId}&grain=${grain}&from=${from}&to=${to}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: TopLinksResponse = await response.json();

      // Extract items array from response
      const items = data.items || [];
      setTopLinksData(items);

      // If we have items, use the first one's campaignId for trends
      if (items.length > 0 && items[0].campaignId) {
        setSelectedCampaignId(items[0].campaignId);
      } else {
        // No items found, clear selected campaign
        setSelectedCampaignId(null);
      }
    } catch (err: any) {
      // console.error('❌ Failed to fetch top links:', err);
      setTopLinksError(err.message || 'Failed to load top links data');
      toast.error('Failed to load top links data');
      // Set empty array on error
      setTopLinksData([]);
      setSelectedCampaignId(null);
    } finally {
      setTopLinksLoading(false);
    }
  }, [getDateRange, getGrain]);

  // Function to fetch trends data
  const fetchTrends = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();

    if (!token || !tenantId) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    // Don't fetch trends if no campaignId is available
    if (!selectedCampaignId) {
      setLoading(false);
      setTrendsData([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { from, to } = getDateRange();
      const grain = getGrain();

      // Construct the API URL with query parameters using the campaignId from top links
      const url = `${BASE_URL}/v1/reporting/analytics/trends?tenantId=${tenantId}&grain=${grain}&from=${from}&to=${to}&campaignId=${selectedCampaignId}`;


      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data: TrendDataPoint[] = await response.json();

      // Set trends data directly (it's an array)
      setTrendsData(data || []);
    } catch (err: any) {
      // console.error('❌ Failed to fetch trends:', err);
      setError(err.message || 'Failed to load trends data');
      toast.error('Failed to load trends data');
      // Set empty array on error
      setTrendsData([]);
    } finally {
      setLoading(false);
    }
  }, [getDateRange, getGrain, selectedCampaignId]);

  // Fetch data when component mounts or range changes
  useEffect(() => {
    fetchTopLinks();
  }, [fetchTopLinks]);

  // Fetch trends when campaignId changes or range changes
  useEffect(() => {
    if (selectedCampaignId) {
      fetchTrends();
    } else {
      // Reset trends data when no campaign is selected
      setTrendsData([]);
      setLoading(false);
    }
  }, [fetchTrends, selectedCampaignId, selectedRange]);

  // Generate chart categories based on data
  const getCategories = useCallback(() => {
    if (trendsData.length === 0) {
      // Return empty array when no data
      return [];
    }

    // Use actual data for categories - use 'start' or 'end' timestamp
    return trendsData.map(item => {
      const date = dayjs(item.start || item.end);
      if (selectedRange === 'PT1H') {
        // For 1 hour, show minutes
        return date.format('HH:mm');
      } else if (selectedRange === 'PT24H') {
        // For 24 hours, show hour
        return date.format('h A');
      } else if (selectedRange === 'PT168H') {
        // For 7 days, show day
        return date.format('ddd');
      } else if (selectedRange === 'PT720H') {
        // For 30 days, show date
        return date.format('MMM D');
      } else {
        // For 1 year, show month
        return date.format('MMM YYYY');
      }
    });
  }, [trendsData, selectedRange]);

  // Prepare chart series from API data
  const getChartSeries = useCallback(() => {
    if (trendsData.length === 0) {
      // Return empty series when no data
      return [
        {
          name: 'Open',
          data: [],
        },
        {
          name: 'Click',
          data: [],
        },
      ];
    }

    return [
      {
        name: 'Open',
        data: trendsData.map(item => item.opens || 0),
      },
      {
        name: 'Click',
        data: trendsData.map(item => item.clicks || 0),
      },
    ];
  }, [trendsData]);

  // Configuration for the ApexCharts line chart
  const campaignTrendsOptions: ApexCharts.ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: { curve: 'smooth', width: 3 },
    markers: { size: 4 },
    xaxis: {
      categories: getCategories(),
      labels: {
        style: { fontSize: '12px', colors: '#9FA9B2' },
        rotate: -45,
        rotateAlways: true,
      },
    },
    colors: ['#FF6B6B', '#4A90E2'],
    legend: { position: 'top', horizontalAlign: 'left' },
    grid: { borderColor: '#f1f1f1' },
    tooltip: {
      y: {
        formatter: (value) => `${value} interactions`,
      },
    },
    yaxis: {
      title: {
        text: 'Interactions',
        style: { fontSize: '12px', color: '#9FA9B2' },
      },
    },
    noData: {
      text: 'No data available',
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: '#9FA9B2',
        fontSize: '14px',
        fontFamily: 'Arial',
      },
    },
  };

  // Transform top links data to display format
  const getTopLinksDisplay = useCallback((): TopLinkDisplay[] => {
    if (topLinksData.length === 0) {
      return [];
    }

    return topLinksData.map(link => {
      // Get the rate value from rightBadge
      const rateValue = link.rightBadge?.value || 0;
      const rateLabel = link.rightBadge?.label || 'Rate';

      // Use label as the title
      let displayName = link.label || 'Link';

      // Truncate if too long
      if (displayName.length > 30) {
        displayName = displayName.substring(0, 27) + '...';
      }

      return {
        title: displayName,
        rate: `${rateValue.toFixed(1)}%`,
        label: rateLabel,
        campaignName: link.campaignName || 'Unknown Campaign',
        campaignId: link.campaignId,
        linkId: link.linkId,
      };
    });
  }, [topLinksData]);

  const chartSeries = getChartSeries();
  const topLinksDisplay = getTopLinksDisplay();

  // Combined loading state
  const isInitialLoading = loading && topLinksLoading;

  // Check if we have data
  const hasTopLinksData = topLinksData.length > 0;
  const hasTrendsData = trendsData.length > 0;

  // Render loading state (only during initial load)
  if (isInitialLoading) {
    return (
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-6'>
        <div className='md:col-span-1'>
          <div className='bg-white p-4 rounded-md shadow-sm border h-full'>
            <div className='h-8 bg-gray-200 rounded w-48 mb-6 animate-pulse'></div>
            <div className='space-y-4'>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className='flex items-center justify-between'>
                  <div className='flex items-center space-x-3'>
                    <div className='h-10 w-32 bg-gray-200 rounded animate-pulse'></div>
                  </div>
                  <div className='h-10 w-20 bg-gray-200 rounded animate-pulse'></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className='md:col-span-2'>
          <div className='bg-white p-4 rounded-md shadow-sm border h-full'>
            <div className='h-8 bg-gray-200 rounded w-48 mb-4 animate-pulse'></div>
            <div className='h-64 bg-gray-200 rounded animate-pulse'></div>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error || topLinksError) {
    return (
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-6'>
        <div className='md:col-span-3'>
          <div className='bg-red-50 border border-red-200 rounded-md p-4 text-center'>
            <p className='text-red-600'>
              {error || topLinksError || 'Unable to load data'}
            </p>
            <button
              onClick={() => {
                fetchTopLinks();
                if (selectedCampaignId) {
                  fetchTrends();
                }
              }}
              className='mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700'
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mt-6'>
      {/* Top Performing Links Section */}
      <div className='md:col-span-1'>
        <div className='bg-white p-4 rounded-md shadow-sm border h-full flex flex-col'>
          <h3 className='font-semibold text-[#1F2937] mb-6 text-[20px]'>
            Top Performing Links & CTA's
          </h3>
          {hasTopLinksData ? (
            <>
              <ul className='space-y-4 text-sm text-gray-700'>
                {topLinksDisplay.slice(0, 4).map((link, idx) => (
                  <li key={link.linkId || idx} className='flex items-center justify-between'>
                    <div className='flex items-center space-x-3'>
                      <div>
                        <p className='font-semibold text-[#1F2937] text-[14px]'>
                          {link.title}
                        </p>
                        <p className='text-[12px] text-[#9FA9B2]'>
                          {link.campaignName}
                        </p>
                      </div>
                      <div className='bg-gray-100 p-2 rounded-lg'>
                        <FiLinkComponent className='text-gray-600' />
                      </div>
                    </div>
                    <div className='text-right'>
                      <p className='font-semibold text-[#1F2937] text-[14px]'>
                        {link.rate}
                      </p>
                      <p className='text-[12px] text-[#9FA9B2]'>{link.label}</p>
                    </div>
                  </li>
                ))}
              </ul>
              {
                topLinksDisplay.length > 5 && (
                  <p className='mt-auto pt-4 text-sm text-[#4A90E2] cursor-pointer font-semibold text-center hover:underline'>
                    View All Links
                  </p>
                )
              }
            </>
          ) : (
            <div className='flex items-center justify-center h-80 text-gray-500'>
              No top performing links data available
            </div>
          )}
        </div>
      </div>

      {/* Campaign Trends Chart Section */}
      <div className='md:col-span-2'>
        <div className='bg-white p-4 rounded-md shadow-sm border h-full'>
          <div className='flex justify-between items-center mb-4'>
            <h3 className='font-semibold text-[#1F2937] text-[20px]'>
              Current Campaign Trends
            </h3>
            <select
              className='border rounded p-2 text-[14px] border-[#E1EAF0] bg-[#F5F8FA] text-[#3F5265] cursor-pointer hover:bg-[#E1EAF0] transition-colors'
              value={selectedRange}
              onChange={(e) => setSelectedRange(e.target.value as 'PT1H' | 'PT24H' | 'PT168H' | 'PT720H' | 'PT8760H')}
            >
              <option value='PT1H'>Last 1 Hour</option>
              <option value='PT24H'>Last 24 Hours</option>
              <option value='PT168H'>Last 7 Days</option>
              <option value='PT720H'>Last 30 Days</option>
              <option value='PT8760H'>Last 1 Year</option>
            </select>
          </div>

          {/* Render chart with data */}
          {selectedCampaignId ? (
            hasTrendsData ? (
              <Chart
                options={campaignTrendsOptions}
                series={chartSeries}
                type='line'
                height={320}
              />
            ) : (
              <div className='h-80 flex items-center justify-center text-gray-500'>
                No campaign trends data available for this campaign
              </div>
            )
          ) : (
            <div className='h-80 flex items-center justify-center text-gray-500'>
              No campaigns available to show trends
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MiddleSection;